import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BootSequence = ({ user, onComplete }) => {
  const [lines, setLines] = useState([]);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('boot'); // boot, security, complete
  const [securityAlert, setSecurityAlert] = useState(null);
  const navigate = useNavigate();

  // Grades selon l'utilisateur
  const getGrade = (userData) => {
    if (userData?.grade) return userData.grade;
    const email = userData?.email;
    if (email?.includes('giraud')) return 'COMMANDANT';
    if (email?.includes('garascio') || email?.includes('garoscio')) return 'LORD';
    return 'OPERATEUR';
  };

  const grade = getGrade(user);

  // Concurrents pour les fausses alertes
  const competitors = [
    'BFM_BUSINESS', 'LES_ECHOS', 'LE_FIGARO_ECO', 
    'CAPITAL', 'CHALLENGES', 'LA_TRIBUNE',
    'BOURSORAMA', 'ZONE_BOURSE', 'TRADINGSAT'
  ];

  // Messages humoristiques aléatoires
  const funnyMessages = [
    { module: 'CANTINE', msg: 'Menu du jour: mystère protéiné...', status: 'ÉVITER' },
    { module: 'CAFÉ', msg: 'Niveau caféine optimal détecté', status: 'APPROUVÉ' },
    { module: 'STAGIAIRE', msg: 'Aucun stagiaire en salle serveur', status: 'MIRACLE' },
    { module: 'IMPRIMANTE', msg: 'Bourrage papier préventif effectué', status: 'HABITUEL' },
    { module: 'WIFI', msg: 'Signal stable (pour une fois)', status: 'SUSPECT' },
    { module: 'PARKING', msg: 'Place n°42 toujours occupée', status: 'MYSTÈRE' },
    { module: 'CLIM', msg: 'Température: guerre civile évitée', status: 'TRÊVE' },
    { module: 'ASCENSEUR', msg: 'Musique d\'ambiance désactivée', status: 'VICTOIRE' },
  ];

  // Séquence de boot
  const bootSequence = [
    { delay: 100, text: '╔════════════════════════════════════════════════════════════╗', type: 'border' },
    { delay: 50, text: '║           AUTO EDITO COMMAND CENTER v3.7.2                  ║', type: 'border' },
    { delay: 50, text: '║              [ MILITARY GRADE PUBLISHING ]                  ║', type: 'border' },
    { delay: 50, text: '╚════════════════════════════════════════════════════════════╝', type: 'border' },
    { delay: 200, text: '', type: 'empty' },
    { delay: 100, text: '[BOOT] Initialisation séquence de démarrage...', type: 'system' },
    { delay: 150, text: '[KERNEL] Chargement noyau quantique... [OK]', type: 'ok' },
    { delay: 120, text: '[MEMORY] Allocation mémoire holographique... [OK]', type: 'ok' },
    { delay: 180, text: '[NEURAL] Calibration réseaux neuronaux... [OK]', type: 'ok' },
    { delay: 100, text: '[AI] Réveil Claude Sonnet 4.5... [OK]', type: 'ok' },
    { delay: 130, text: '[GEMINI] Synchronisation Nano Banana... [OK]', type: 'ok' },
    { delay: 90, text: '[CRYPTO] Vérification certificats SSL/TLS... [OK]', type: 'ok' },
    { delay: 200, text: '', type: 'empty' },
    { delay: 100, text: `[AUTH] Identification biométrique: ${user?.full_name || 'UNKNOWN'}`, type: 'info' },
    { delay: 150, text: `[RANK] Grade détecté: ${grade}`, type: 'grade' },
    { delay: 120, text: `[CLEARANCE] Niveau d'accréditation: ALPHA-7`, type: 'info' },
    { delay: 200, text: '', type: 'empty' },
    { delay: 100, text: '[WORDPRESS] Connexion satellites éditoriaux...', type: 'system' },
    { delay: 80, text: '  ├─ ECONOMIEMATIN.FR... [ONLINE]', type: 'sub-ok' },
    { delay: 60, text: '  ├─ ARMEES.COM... [ONLINE]', type: 'sub-ok' },
    { delay: 70, text: '  ├─ LENERGEEK.COM... [ONLINE]', type: 'sub-ok' },
    { delay: 50, text: '  └─ POLITIQUEMATIN.FR... [ONLINE]', type: 'sub-ok' },
    { delay: 200, text: '', type: 'empty' },
    { delay: 100, text: `[${funnyMessages[Math.floor(Math.random() * funnyMessages.length)].module}] ${funnyMessages[Math.floor(Math.random() * funnyMessages.length)].msg}... [${funnyMessages[Math.floor(Math.random() * funnyMessages.length)].status}]`, type: 'funny' },
    { delay: 200, text: '', type: 'empty' },
    { delay: 100, text: '[FIREWALL] Analyse périmètre de sécurité...', type: 'warning' },
  ];

  // Génération alerte sécurité
  const generateSecurityAlert = () => {
    const competitor = competitors[Math.floor(Math.random() * competitors.length)];
    return {
      competitor,
      lines: [
        { delay: 100, text: '', type: 'empty' },
        { delay: 50, text: '⚠️ ═══════════════════════════════════════════════════════ ⚠️', type: 'alert-border' },
        { delay: 50, text: '        ALERTE SÉCURITÉ NIVEAU MAXIMUM', type: 'alert-title' },
        { delay: 50, text: '⚠️ ═══════════════════════════════════════════════════════ ⚠️', type: 'alert-border' },
        { delay: 200, text: '', type: 'empty' },
        { delay: 150, text: `[DÉTECTION] Tentative d'intrusion: ${competitor}`, type: 'alert' },
        { delay: 200, text: '[FIREWALL] Analyse signature malveillante...', type: 'warning' },
        { delay: 300, text: '[DÉFENSE] Déploiement pare-feu quantique...', type: 'system' },
        { delay: 400, text: '[COUNTER] Lancement contre-mesures...', type: 'system' },
        { delay: 300, text: '', type: 'empty' },
        { delay: 100, text: '✅ INTRUSION NEUTRALISÉE', type: 'success' },
        { delay: 150, text: `[LOG] ${competitor} ajouté à la liste des losers`, type: 'funny' },
        { delay: 200, text: '', type: 'empty' },
      ]
    };
  };

  // Séquence finale
  const finalSequence = [
    { delay: 100, text: '[SYSTEMS] Tous les systèmes opérationnels', type: 'ok' },
    { delay: 150, text: '', type: 'empty' },
    { delay: 100, text: '════════════════════════════════════════════════════════════', type: 'border' },
    { delay: 50, text: `   BIENVENUE ${grade} ${(user?.full_name || 'OPÉRATEUR').toUpperCase()}`, type: 'welcome' },
    { delay: 50, text: '   CENTRE DE COMMANDE PRÊT', type: 'ready' },
    { delay: 50, text: '════════════════════════════════════════════════════════════', type: 'border' },
    { delay: 200, text: '', type: 'empty' },
    { delay: 100, text: '[REDIRECT] Transfert vers le centre de commande dans 3...', type: 'countdown' },
  ];

  useEffect(() => {
    let currentIndex = 0;
    let currentLines = [];
    let allSequence = [...bootSequence];

    const processLine = () => {
      if (currentIndex >= allSequence.length) {
        // Phase transition
        if (phase === 'boot') {
          setPhase('security');
          const alert = generateSecurityAlert();
          setSecurityAlert(alert);
          allSequence = [...allSequence, ...alert.lines, ...finalSequence];
        } else if (phase === 'security' && currentIndex >= allSequence.length) {
          // Countdown and redirect
          setTimeout(() => {
            setLines(prev => [...prev, { text: '[REDIRECT] ...2...', type: 'countdown' }]);
            setTimeout(() => {
              setLines(prev => [...prev, { text: '[REDIRECT] ...1...', type: 'countdown' }]);
              setTimeout(() => {
                setLines(prev => [...prev, { text: '[REDIRECT] LANCEMENT!', type: 'success' }]);
                setTimeout(() => {
                  onComplete();
                }, 500);
              }, 800);
            }, 800);
          }, 800);
          return;
        }
      }

      if (currentIndex < allSequence.length) {
        const line = allSequence[currentIndex];
        currentLines = [...currentLines, line];
        setLines([...currentLines]);
        setProgress(Math.min(100, Math.floor((currentIndex / allSequence.length) * 100)));
        currentIndex++;
        setTimeout(processLine, line.delay);
      }
    };

    setTimeout(processLine, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effet après phase security
  useEffect(() => {
    if (phase === 'security' && securityAlert) {
      let extendedSequence = [...bootSequence, ...securityAlert.lines, ...finalSequence];
      let idx = lines.length;
      
      const continueSequence = () => {
        if (idx < extendedSequence.length) {
          const line = extendedSequence[idx];
          setLines(prev => [...prev, line]);
          setProgress(Math.min(100, Math.floor((idx / extendedSequence.length) * 100)));
          idx++;
          setTimeout(continueSequence, line.delay);
        } else {
          // Countdown
          setTimeout(() => {
            setLines(prev => [...prev, { text: '[REDIRECT] ...2...', type: 'countdown' }]);
            setTimeout(() => {
              setLines(prev => [...prev, { text: '[REDIRECT] ...1...', type: 'countdown' }]);
              setTimeout(() => {
                setLines(prev => [...prev, { text: '[REDIRECT] LANCEMENT!', type: 'success' }]);
                setTimeout(() => onComplete(), 500);
              }, 800);
            }, 800);
          }, 800);
        }
      };

      continueSequence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, securityAlert]);

  const getLineClass = (type) => {
    switch (type) {
      case 'border': return 'text-sky-600';
      case 'system': return 'text-slate-400';
      case 'ok': return 'text-green-400';
      case 'sub-ok': return 'text-green-300 pl-4';
      case 'info': return 'text-sky-400';
      case 'grade': return 'text-yellow-400 font-bold';
      case 'warning': return 'text-yellow-500';
      case 'alert': return 'text-red-500 font-bold';
      case 'alert-border': return 'text-red-500';
      case 'alert-title': return 'text-red-400 font-bold text-center animate-pulse';
      case 'success': return 'text-green-400 font-bold';
      case 'funny': return 'text-purple-400 italic';
      case 'welcome': return 'text-sky-300 font-bold text-lg';
      case 'ready': return 'text-green-400 font-bold';
      case 'countdown': return 'text-yellow-400';
      default: return 'text-slate-500';
    }
  };

  // Progress bar ASCII
  const progressBar = () => {
    const filled = Math.floor(progress / 5);
    const empty = 20 - filled;
    return '▓'.repeat(filled) + '░'.repeat(empty);
  };

  return (
    <div className="min-h-screen bg-black p-4 md:p-8 font-mono text-sm overflow-hidden">
      {/* Scanlines overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-10 z-50"
           style={{
             backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,255,0.03) 2px, rgba(0,255,255,0.03) 4px)'
           }}
      />
      
      {/* CRT glow effect */}
      <div className="fixed inset-0 pointer-events-none z-40"
           style={{
             boxShadow: 'inset 0 0 100px rgba(0,229,255,0.1)'
           }}
      />

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Terminal window */}
        <div className="bg-slate-950/90 border border-sky-900/50 rounded-lg p-6 shadow-2xl shadow-sky-500/10">
          {/* Terminal header */}
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-800">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="ml-4 text-slate-500 text-xs">AUTO_EDITO_TERMINAL — bash — 80×24</span>
          </div>

          {/* Terminal content */}
          <div className="h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-sky-900 scrollbar-track-transparent">
            {lines.map((line, index) => (
              <div 
                key={index} 
                className={`${getLineClass(line.type)} leading-relaxed whitespace-pre`}
                style={{ animation: 'fadeIn 0.1s ease-in' }}
              >
                {line.text}
              </div>
            ))}
            <span className="inline-block w-2 h-4 bg-sky-400 animate-pulse ml-1">_</span>
          </div>

          {/* Progress bar */}
          <div className="mt-4 pt-4 border-t border-slate-800">
            <div className="flex items-center gap-4">
              <span className="text-sky-400 text-xs">BOOT PROGRESS:</span>
              <span className="text-sky-300 font-bold tracking-wider">[{progressBar()}]</span>
              <span className="text-sky-400">{progress}%</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 text-center text-slate-600 text-xs">
          © 2026 Jean-Baptiste Giraud & Paolo Garoscio & LPP Logiciel — Tous droits réservés
          <br />
          <span className="text-slate-700">Flux capaciteur: 1.21 GW | Réponse ultime: 42</span>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default BootSequence;
